import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { CHALLAN_TYPE_LABEL, challanPdfFileName, fetchChallanPdfBlob, saveBlobAs } from './challanUtils';

const PREVIEW_ICON = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

/**
 * Shows the challan PDF exactly as it downloads (same file from the server), with a Download button
 * that saves the file already loaded.
 * @param {{ open: boolean, challan: object|null, copy?: string, onClose: () => void }} props
 */
export default function ChallanPreviewModal({ open, challan, copy, onClose }) {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !challan) return undefined;
    let cancelled = false;
    let objectUrl = '';
    setLoading(true);
    setError('');
    setBlob(null);
    setUrl('');
    fetchChallanPdfBlob(challan, copy)
      .then((b) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(b);
        setBlob(b);
        setUrl(objectUrl);
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'PDF could not be loaded'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, challan, copy]);

  if (!challan) return null;

  const title = `Challan No. ${challan.challanNo}${challan.financialYear ? ` · FY ${challan.financialYear}` : ''}`;
  const description = copy
    ? CHALLAN_TYPE_LABEL[copy] || copy
    : (challan.parties || []).map((p) => CHALLAN_TYPE_LABEL[p.category] || p.category).join(' · ');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={title}
      description={description}
      icon={PREVIEW_ICON}
      bodyClassName="!p-0 bg-slate-100"
      footer={(
        <>
          <Button type="button" variant="secondary" onClick={onClose}>Close</Button>
          <Button
            type="button"
            variant="primary"
            disabled={!blob}
            onClick={() => blob && saveBlobAs(blob, challanPdfFileName(challan, copy))}
          >
            Download PDF
          </Button>
        </>
      )}
    >
      <div className="w-full" style={{ height: '72vh' }}>
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">Loading challan…</div>
        ) : error ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-rose-600">{error}</div>
        ) : url ? (
          <iframe title={title} src={`${url}#view=FitH`} className="h-full w-full border-0 bg-white" />
        ) : null}
      </div>
    </Modal>
  );
}
